import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import OrderTraceModal from '../components/OrderTraceModal';
import ProductTraceModal from '../components/ProductTraceModal';

// ── Nav cards ─────────────────────────────────────────────────────────────────
const NAV_CARDS = [
  {
    path:        '/dispatch-tools',
    icon:        '🚚',
    title:       'Dispatch Tools',
    description: 'Smart Buyer purchasing and the Ready to Dispatch worklists.',
    accent:      'green',
  },
  {
    path:        '/frontline-tools',
    icon:        '🧑‍💼',
    title:       'Frontline Tools',
    description: 'Received POs, open PO worklist, problem orders and product margins.',
    accent:      'gold',
  },
  {
    path:        '/customer-orders-no-stock',
    icon:        '📊',
    title:       'Customer Orders — No Stock',
    description: 'Open approved orders where available stock cannot fulfil the outstanding quantity.',
    accent:      'gold',
  },
  {
    path:        '/control-panel',
    icon:        '🎛️',
    title:       'Control Panel',
    description: 'Turn per-supplier automation (Smart Buyer, Auto Stock Refresh) on or off.',
    accent:      'blue',
  },
  {
    path:        '/utilities',
    icon:        '🧰',
    title:       'Utilities',
    description: 'Help searches and lookups — e.g. find the received PO that matches an old order PO.',
    accent:      'blue',
  },
  {
    path:        '/reports',
    icon:        '📑',
    title:       'Reports & Excel Stuff',
    description: 'Build reports and download them as Excel — e.g. the Traxxas Models Forecast.',
    accent:      'green',
  },
];

// ── Status Indicator Card ─────────────────────────────────────────────────────
const StatusCard = ({ title, icon, healthy, detail, path, loading }) => {
  const navigate = useNavigate();
  const dotColor = loading ? 'var(--muted)' : healthy ? '#4ade80' : '#f87171';
  const dotLabel = loading ? '…' : healthy ? 'All OK' : 'Needs attention';

  return (
    <button
      onClick={() => navigate(path)}
      style={{
        flex: 1,
        minWidth: 220,
        background: 'var(--bg2)',
        border: `1px solid ${loading ? 'var(--border)' : healthy ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.35)'}`,
        borderRadius: 10,
        padding: '18px 22px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'border-color 0.2s, background 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <div style={{
        width: 14, height: 14, borderRadius: '50%',
        background: dotColor,
        flexShrink: 0,
        boxShadow: loading ? 'none' : healthy
          ? '0 0 8px rgba(74,222,128,0.6)'
          : '0 0 8px rgba(248,113,113,0.6)',
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 3 }}>
          {icon} {title}
        </div>
        <div style={{ fontSize: 11, color: loading ? 'var(--muted)' : healthy ? '#4ade80' : '#f87171' }}>
          {loading ? 'Loading…' : dotLabel}
          {!loading && detail && (
            <span style={{ color: 'var(--muted)', marginLeft: 6 }}>{detail}</span>
          )}
        </div>
      </div>
      <div style={{ fontSize: 14, color: 'var(--muted)' }}>→</div>
    </button>
  );
};

// ── SOH Feed Status ───────────────────────────────────────────────────────────
const SOH_FEEDS = [
  { label: 'Bringans',     table: 'bringans_soh' },
  { label: 'Geek Culture', table: 'geek_culture_soh' },
  { label: 'Toymod',       table: 'toymod_soh' },
  { label: 'Promaster',    table: 'promaster_soh' },
  { label: 'SI Wholesale', table: 'siw_soh' },
  { label: 'HOT',          table: 'hot_soh' },
];

const SohFeedStatus = () => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const results = await Promise.all(
        SOH_FEEDS.map(async ({ label, table }) => {
          const { data } = await supabase
            .from(table)
            .select('load_datetime')
            .order('load_datetime', { ascending: false })
            .limit(1);
          const raw = data?.[0]?.load_datetime ?? null;
          return { label, raw };
        })
      );
      setRows(results);
      setLoading(false);
    };
    load();
  }, []);

  const fmt = (raw) => {
    if (!raw) return { time: '—', stale: true };
    const d   = new Date(raw);
    const age = (Date.now() - d.getTime()) / 3600000; // hours
    const time = d.toLocaleTimeString('en-NZ', {
      timeZone: 'Pacific/Auckland',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const dateStr = d.toLocaleDateString('en-NZ', {
      timeZone: 'Pacific/Auckland',
      day: '2-digit', month: 'short',
    });
    return { time, dateStr, stale: age > 24 * 8 }; // 8 days -- SOH feeds run on a weekly-ish cadence, not daily
  };

  return (
    <div style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '16px 20px',
      flex: 1,
      minWidth: 220,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 12 }}>
        📡 SOH Feed Status
      </div>
      {loading ? (
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {rows.map(({ label, raw }) => {
            const { time, dateStr, stale } = fmt(raw);
            return (
              <div key={label} style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 8,
              }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', minWidth: 90 }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: stale ? '#f87171' : '#4ade80',
                    boxShadow: stale ? '0 0 5px rgba(248,113,113,0.5)' : '0 0 5px rgba(74,222,128,0.5)',
                    flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 11, color: stale ? '#f87171' : 'var(--white)', fontVariantNumeric: 'tabular-nums' }}>
                    {time}
                  </span>
                  {dateStr && (
                    <span style={{ fontSize: 10, color: 'var(--muted)' }}>{dateStr}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Smart Buyer Orders (last run per supplier) ────────────────────────────────
const SmartBuyerRecent = () => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('smart_buyer_orders')
        .select('created_at, supplier_name')
        .order('created_at', { ascending: false });

      // Reduce to most-recent run per supplier
      const seen = new Map();
      for (const r of (data || [])) {
        if (!seen.has(r.supplier_name)) seen.set(r.supplier_name, r.created_at);
      }
      const result = Array.from(seen.entries())
        .map(([supplier_name, created_at]) => ({ supplier_name, created_at }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setRows(result);
      setLoading(false);
    };
    load();
  }, []);

  const fmt = (ts) => {
    const d = new Date(ts);
    const age = (Date.now() - d.getTime()) / 3600000;
    const time = d.toLocaleTimeString('en-NZ', {
      timeZone: 'Pacific/Auckland',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const dateStr = d.toLocaleDateString('en-NZ', {
      timeZone: 'Pacific/Auckland',
      day: '2-digit', month: 'short',
    });
    return { time, dateStr, stale: age > 25 };
  };

  // Shorten long supplier names
  const short = (name) => name
    .replace(' (As A Supplier)', '')
    .replace(' Ltd', '')
    .replace(' Limited', '')
    .replace('Trevor ', '')
    .replace('South Island ', 'SI ');

  return (
    <div style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '16px 20px',
      flex: 1,
      minWidth: 220,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 12 }}>
        🛒 Smart Buyer Orders
      </div>
      {loading ? (
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>No orders found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {rows.map(({ supplier_name, created_at }) => {
            const { time, dateStr, stale } = fmt(created_at);
            return (
              <div key={supplier_name} style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 8,
              }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', minWidth: 90 }}>{short(supplier_name)}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: stale ? '#f87171' : '#4ade80',
                    boxShadow: stale ? '0 0 5px rgba(248,113,113,0.5)' : '0 0 5px rgba(74,222,128,0.5)',
                    flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 11, color: stale ? '#f87171' : 'var(--white)', fontVariantNumeric: 'tabular-nums' }}>
                    {time}
                  </span>
                  {dateStr && (
                    <span style={{ fontSize: 10, color: 'var(--muted)' }}>{dateStr}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Dispatch Throughput Chart ─────────────────────────────────────────────────
const DispatchThroughput = ({
  title       = 'Dispatch Throughput',
  rpcName     = 'get_dispatch_throughput_hourly',
  detailMode  = null,
}) => {
  const navigate = useNavigate();
  const todayStr = () => new Date().toLocaleDateString('en-CA');

  const [date, setDate]             = useState(todayStr());
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc(rpcName, {
        p_date: d,
      });
      if (err) throw err;
      setRows(data || []);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message || 'Failed to load data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [rpcName]);

  useEffect(() => { load(date); }, [date, load]);

  // Derived summary
  const latest     = rows.length ? rows[rows.length - 1] : null;
  const totalReady = latest?.cumulative_ready ?? 0;
  const dispatched = latest?.dispatched ?? 0;
  const pending    = latest?.pending ?? 0;
  const backordered = latest?.backordered;
  const pct        = totalReady > 0 ? Math.round((dispatched / totalReady) * 100) : 0;
  // Move tracking — only present on the partial chart's RPC
  const hasMove    = latest?.cumulative_to_move != null;
  const toMove     = latest?.cumulative_to_move ?? 0;
  const moved      = latest?.moved ?? 0;

  // Chart dimensions
  const chartH = 140;
  const chartW = 600;
  const padL   = 36;
  const padR   = 12;
  const padT   = 12;
  const padB   = 28;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const maxVal = rows.length ? Math.max(...rows.map(r => Math.max(r.cumulative_ready, r.cumulative_to_move ?? 0)), 1) : 1;
  const xStep  = rows.length > 1 ? innerW / (rows.length - 1) : innerW;

  const toX = (i)   => padL + i * xStep;
  const toY = (val) => padT + innerH - (val / maxVal) * innerH;

  const readyPath = rows.map((r, i) =>
    `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(r.cumulative_ready).toFixed(1)}`
  ).join(' ');

  const dispatchedPath = rows.map((r, i) =>
    `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(r.dispatched).toFixed(1)}`
  ).join(' ');

  const toMovePath = rows.map((r, i) =>
    `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(r.cumulative_to_move ?? 0).toFixed(1)}`
  ).join(' ');

  const movedPath = rows.map((r, i) =>
    `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(r.moved ?? 0).toFixed(1)}`
  ).join(' ');

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(frac => ({
    y:     padT + innerH - frac * innerH,
    label: Math.round(frac * maxVal),
  }));

  const COL_READY      = '#C8922A';
  const COL_DISPATCHED = '#4ade80';
  const COL_PENDING    = '#f87171';
  const COL_TO_MOVE    = '#60a5fa';
  const COL_MOVED      = '#a78bfa';

  return (
    <div style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '20px 24px',
    }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap',
        gap: 12, marginBottom: 18,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>📬</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>
            {title}
          </span>
          {lastRefresh && (
            <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 4 }}>
              updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => navigate(`/dispatch-drilldown?date=${date}${detailMode ? `&mode=${detailMode}` : ''}`)}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--muted)',
              fontSize: 11,
              padding: '3px 10px',
              cursor: 'pointer',
              marginLeft: 4,
              transition: 'color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { e.target.style.color = 'var(--white)'; e.target.style.borderColor = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={e => { e.target.style.color = 'var(--muted)'; e.target.style.borderColor = 'var(--border)'; }}
          >
            View detail →
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--white)',
              fontSize: 12,
              padding: '4px 8px',
              cursor: 'pointer',
            }}
          />
          <button
            onClick={() => load(date)}
            disabled={loading}
            style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: loading ? 'var(--muted)' : 'var(--white)',
              fontSize: 12,
              padding: '4px 10px',
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* KPI summary */}
      {!loading && !error && totalReady > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          {[
            { label: 'Ready (cumulative)', value: totalReady, color: COL_READY },
            { label: 'Dispatched',         value: dispatched,  color: COL_DISPATCHED },
            { label: 'Pending',            value: pending,     color: COL_PENDING },
            {
              label: 'Rate',
              value: `${pct}%`,
              color: pct >= 80 ? COL_DISPATCHED : pct >= 50 ? COL_READY : COL_PENDING,
            },
            ...(backordered != null
              ? [{ label: 'Backordered', value: backordered, color: 'var(--muted)' }]
              : []),
            ...(hasMove
              ? [
                  { label: 'Items to move (cumulative)', value: toMove,          color: COL_TO_MOVE },
                  { label: 'Moved',                      value: moved,           color: COL_MOVED },
                  { label: 'Still to move',              value: toMove - moved,  color: toMove - moved > 0 ? COL_PENDING : 'var(--muted)' },
                ]
              : []),
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '10px 16px',
              minWidth: 90,
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Loading / error / empty states */}
      {loading && (
        <div style={{ height: chartH, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 12 }}>
          Loading…
        </div>
      )}
      {!loading && error && (
        <div style={{ height: chartH, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', fontSize: 12 }}>
          {error}
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div style={{ height: chartH, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 12 }}>
          No dispatch data for this date.
        </div>
      )}

      {/* Chart */}
      {!loading && !error && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <svg
            viewBox={`0 0 ${chartW} ${chartH}`}
            style={{ width: '100%', minWidth: 320, height: 'auto', display: 'block' }}
          >
            {/* Grid */}
            {gridLines.map(({ y, label }) => (
              <g key={y}>
                <line x1={padL} y1={y} x2={chartW - padR} y2={y}
                  stroke="var(--border)" strokeWidth={0.5} strokeDasharray="3,3" />
                <text x={padL - 4} y={y + 3.5} textAnchor="end" fontSize={8} fill="var(--muted)">
                  {label}
                </text>
              </g>
            ))}

            {/* X axis labels — every other hour */}
            {rows.map((r, i) => i % 2 === 0 ? (
              <text key={r.hour_nzt} x={toX(i)} y={chartH - 4}
                textAnchor="middle" fontSize={8} fill="var(--muted)">
                {r.hour_nzt}
              </text>
            ) : null)}

            {/* Dispatched area fill */}
            {rows.length > 1 && (
              <path
                d={`${dispatchedPath} L${toX(rows.length - 1).toFixed(1)},${(padT + innerH).toFixed(1)} L${padL.toFixed(1)},${(padT + innerH).toFixed(1)} Z`}
                fill={COL_DISPATCHED} fillOpacity={0.08}
              />
            )}

            {/* Ready line */}
            {rows.length > 1 && (
              <path d={readyPath} fill="none" stroke={COL_READY}
                strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            )}

            {/* Dispatched line */}
            {rows.length > 1 && (
              <path d={dispatchedPath} fill="none" stroke={COL_DISPATCHED}
                strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            )}

            {/* Move lines (partial chart only) — dashed to separate from ready/dispatched */}
            {hasMove && rows.length > 1 && (
              <>
                <path d={toMovePath} fill="none" stroke={COL_TO_MOVE}
                  strokeWidth={2} strokeDasharray="5,3" strokeLinejoin="round" strokeLinecap="round" />
                <path d={movedPath} fill="none" stroke={COL_MOVED}
                  strokeWidth={2} strokeDasharray="5,3" strokeLinejoin="round" strokeLinecap="round" />
              </>
            )}

            {/* End dots */}
            {rows.length > 0 && (() => {
              const last = rows[rows.length - 1];
              const li   = rows.length - 1;
              return (
                <>
                  <circle cx={toX(li)} cy={toY(last.cumulative_ready)} r={3} fill={COL_READY} />
                  <circle cx={toX(li)} cy={toY(last.dispatched)}        r={3} fill={COL_DISPATCHED} />
                  {hasMove && (
                    <>
                      <circle cx={toX(li)} cy={toY(last.cumulative_to_move ?? 0)} r={3} fill={COL_TO_MOVE} />
                      <circle cx={toX(li)} cy={toY(last.moved ?? 0)}              r={3} fill={COL_MOVED} />
                    </>
                  )}
                </>
              );
            })()}
          </svg>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginTop: 6, justifyContent: 'flex-end' }}>
            {[
              { color: COL_READY,      label: 'Cumulative ready' },
              { color: COL_DISPATCHED, label: 'Dispatched' },
              ...(hasMove
                ? [
                    { color: COL_TO_MOVE, label: 'Items to move (cumulative)' },
                    { color: COL_MOVED,   label: 'Moved' },
                  ]
                : []),
            ].map(({ color, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 20, height: 2, background: color, borderRadius: 1 }} />
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Home Page ─────────────────────────────────────────────────────────────────
const Home = () => {
  const navigate = useNavigate();

  const [syncHealthy, setSyncHealthy]     = useState(true);
  const [syncDetail, setSyncDetail]       = useState('');
  const [loadingSync, setLoadingSync]     = useState(true);

  const [eventsHealthy, setEventsHealthy] = useState(true);
  const [eventsDetail, setEventsDetail]   = useState('');
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [blockedStats, setBlockedStats] = useState({
    'Problem Encountered': { total: 0, needingAttention: 0, scheduled: 0, resolved: 0 },
    'To Call Customer':    { total: 0, needingAttention: 0, scheduled: 0, resolved: 0 },
  });
  const [loadingBlocked, setLoadingBlocked] = useState(true);

  const [traceInput, setTraceInput] = useState('');
  const [traceRef, setTraceRef]     = useState(null);
  const [productInput, setProductInput] = useState('');
  const [productCode, setProductCode]   = useState(null);

  const loadSync = useCallback(async () => {
    setLoadingSync(true);
    const { data } = await supabase
      .from('v_event_log_latest')
      .select('job_key, status')
      .eq('category', 'operational');
    const latest = data || [];
    const errors = latest.filter(r => r.status !== 'ok');
    setSyncHealthy(errors.length === 0);
    setSyncDetail(errors.length > 0
      ? `${errors.length} feed${errors.length > 1 ? 's' : ''} with errors`
      : `${latest.length} feeds healthy`
    );
    setLoadingSync(false);
  }, []);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    const { data } = await supabase
      .from('v_event_log_unresolved')
      .select('id')
      .eq('category', 'automation');
    const count = (data || []).length;
    setEventsHealthy(count === 0);
    setEventsDetail(count > 0
      ? `${count} unresolved`
      : 'Nothing needs attention'
    );
    setLoadingEvents(false);
  }, []);

  const loadBlocked = useCallback(async () => {
    setLoadingBlocked(true);

    // Computes + snapshots today's metrics server-side (also feeds
    // blocked_order_daily_metrics for historical trend/target tracking).
    const { data: rows, error } = await supabase.rpc('record_blocked_order_daily_metrics');

    const stats = {
      'Problem Encountered': { total: 0, needingAttention: 0, scheduled: 0, resolved: 0 },
      'To Call Customer':    { total: 0, needingAttention: 0, scheduled: 0, resolved: 0 },
    };
    if (!error) {
      for (const row of (rows || [])) {
        if (!stats[row.stage]) continue;
        stats[row.stage] = {
          total:            row.total_orders,
          needingAttention: row.needing_attention_count,
          scheduled:        row.scheduled_count,
          resolved:         row.resolved_count,
        };
      }
    }

    setBlockedStats(stats);
    setLoadingBlocked(false);
  }, []);

  useEffect(() => { loadSync(); loadEvents(); loadBlocked(); }, [loadSync, loadEvents, loadBlocked]);

  return (
    <main className="app-main">

      {/* Trace Order / Product Trace */}
      <div style={{ padding: '18px 32px 8px', display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)' }}>🔍 Trace Order</span>
          <input
            type="text"
            placeholder="e.g. OJUN84407-33"
            value={traceInput}
            onChange={e => setTraceInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && traceInput.trim()) setTraceRef(traceInput.trim()); }}
            style={{
              background: 'var(--bg3)', color: 'var(--white)',
              border: '1px solid var(--border2)', borderRadius: 4,
              padding: '8px 14px', fontFamily: 'var(--font-body)',
              fontSize: 14, width: 230,
            }}
          />
          <button
            className="action-btn action-btn--ghost"
            style={{ fontSize: 14, padding: '8px 18px' }}
            disabled={!traceInput.trim()}
            onClick={() => setTraceRef(traceInput.trim())}
          >
            Trace
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)' }}>📦 Trace Product</span>
          <input
            type="text"
            placeholder="e.g. TAM18662"
            value={productInput}
            onChange={e => setProductInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && productInput.trim()) setProductCode(productInput.trim()); }}
            style={{
              background: 'var(--bg3)', color: 'var(--white)',
              border: '1px solid var(--border2)', borderRadius: 4,
              padding: '8px 14px', fontFamily: 'var(--font-body)',
              fontSize: 14, width: 230,
            }}
          />
          <button
            className="action-btn action-btn--ghost"
            style={{ fontSize: 14, padding: '8px 18px' }}
            disabled={!productInput.trim()}
            onClick={() => setProductCode(productInput.trim())}
          >
            Trace
          </button>
        </div>
      </div>
      {traceRef && (
        <OrderTraceModal orderRef={traceRef} onClose={() => setTraceRef(null)} />
      )}
      {productCode && (
        <ProductTraceModal itemCode={productCode} onClose={() => setProductCode(null)} />
      )}

      {/* Status indicators */}
      <div style={{ padding: '24px 32px 8px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <StatusCard
            title="Operational Events"
            icon="⚡"
            healthy={syncHealthy}
            detail={syncDetail}
            path="/replication-status"
            loading={loadingSync}
          />
          <StatusCard
            title="Automation Events"
            icon="📋"
            healthy={eventsHealthy}
            detail={eventsDetail}
            path="/automation-events"
            loading={loadingEvents}
          />
        </div>
      </div>

      {/* Blocked Orders */}
      <div style={{ padding: '8px 32px 8px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { stage: 'Problem Encountered', icon: '🚧' },
            { stage: 'To Call Customer',    icon: '📞' },
          ].map(({ stage, icon }) => {
            const { total, needingAttention, scheduled, resolved } = blockedStats[stage];
            return (
              <StatusCard
                key={stage}
                title={stage}
                icon={icon}
                healthy={total === 0 || needingAttention === 0}
                detail={`${total} in total · ${needingAttention} needing attention · ${scheduled} scheduled · ${resolved} resolved today`}
                path="/blocked-orders"
                loading={loadingBlocked}
              />
            );
          })}
        </div>
      </div>

      {/* SOH Feed Status + Smart Buyer Recent */}
      <div style={{ padding: '8px 32px 8px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <SohFeedStatus />
          <SmartBuyerRecent />
        </div>
      </div>

      {/* Dispatch Throughput */}
      <div style={{ padding: '8px 32px 8px' }}>
        <DispatchThroughput />
      </div>

      {/* Partial Order Throughput */}
      <div style={{ padding: '8px 32px 8px' }}>
        <DispatchThroughput
          title="Partial Order Throughput"
          rpcName="get_partial_dispatch_throughput_hourly"
          detailMode="partial"
        />
      </div>

      {/* Nav cards */}
      <div className="home-greeting">
        <p className="home-greeting__sub">Select a function to get started</p>
      </div>
      <div className="home-cards">
        {NAV_CARDS.map((card) => (
          <button
            key={card.path}
            className={`home-card home-card--${card.accent}`}
            onClick={() => navigate(card.path)}
          >
            <div className="home-card__icon">{card.icon}</div>
            <div className="home-card__body">
              <div className="home-card__title">{card.title}</div>
              <div className="home-card__desc">{card.description}</div>
            </div>
            <div className="home-card__arrow">→</div>
          </button>
        ))}
      </div>

    </main>
  );
};

export default Home;
